/**
 * The edits the graph window makes at the end of a gesture: creating a link of the kind
 * chosen in the menu, and moving an end of a link to another node. They go to the core
 * through `actions`; the drawing follows the core's answer.
 */
import type { Actions } from "../../client/actions";
import type { KindsService } from "../../state/kinds";
import { toast } from "../../shared/toast";
import type { Scene, SimNode } from "../../state/scene";
import type { View } from "../../state/view";
import type { Reconnecting } from "./picture";

/** What the edits need. */
export interface EditDeps {
  scene: Scene;
  actions: Actions;
  view: View;
  /** The kinds links can be made of, built-in and custom. */
  kinds: Pick<KindsService, "links" | "ofLink" | "addLink" | "toDefine">;
}

/** A node's name, or its id when it is not in the scene. */
export function nameOf(scene: Scene, id: string): string {
  return scene.byId.get(id)?.node.name ?? id;
}

/** Creates a link of the kind `kindId` (a plugin's is defined first) and makes sure its kind is shown. */
export async function createLink({ actions, view, kinds }: EditDeps, from: SimNode, kindId: string, to: SimNode): Promise<void> {
  const kind = kinds.links().find((k) => k.id === kindId);
  if (!kind) return;
  const changes = kinds.toDefine([], [kind.id]).length
    ? await kinds.addLink({ from: from.id, to: to.id, kind: kind.id, label: "" })
    : await actions.addLink(from.id, kind.id, to.id);
  if (!changes) return;
  view.showLinkKind(kind.id);
  toast(`${from.node.name} ${kind.verb} ${to.node.name}.`);
}

/** Moves the dragged end of a link to the node it was dropped on, keeping it selected. */
export async function reconnectLink({ scene, actions, view, kinds }: EditDeps, r: Reconnecting): Promise<void> {
  const to = r.to!;
  const end = r.end === "a" ? r.link.a : r.link.b;
  if (to === end) return;
  const changes = await actions.updateLink(r.link.link, r.end === "a" ? { from: to.id } : { to: to.id });
  const updated = changes?.find((c) => c.type === "link_updated");
  if (updated?.type !== "link_updated") return;
  view.selectLink(updated.after);
  toast(`Link now reads ${nameOf(scene, updated.after.from)} ${kinds.ofLink(updated.after).verb} ${nameOf(scene, updated.after.to)}.`);
}
